import emailjs from "emailjs-com";
import { Github, Linkedin, Mail, Send } from "lucide-react";
import { useState } from "react";
import { toast } from "react-hot-toast";
import { contact } from "../utils/data";

const field =
  "w-full border border-paper-300 bg-paper-100 px-4 py-3.5 text-body text-ink-950 placeholder-ink-400 transition-colors duration-200 focus:border-clay";

const Contact = () => {
  const [isSending, setIsSending] = useState(false);

  // CRA only inlines env vars prefixed with REACT_APP_ — without the prefix
  // these are always undefined in the browser.
  const EMAILJS_SERVICE_ID = process.env.REACT_APP_EMAILJS_SERVICE_ID;
  const EMAILJS_TEMPLATE_ID = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;
  const EMAILJS_USER_ID = process.env.REACT_APP_EMAILJS_USER_ID;

  const sendEmail = (e) => {
    e.preventDefault();
    setIsSending(true);

    const form = e.target;
    const name = form.elements["name"].value;
    const email = form.elements["email"].value;
    const message = form.elements["message"].value;

    if (!email || !name || !message) {
      toast.error("Please fill all fields.");
      setIsSending(false);
      return;
    }

    // EmailJS is optional: without config, fail gracefully instead of crashing.
    if (!EMAILJS_SERVICE_ID || !EMAILJS_TEMPLATE_ID || !EMAILJS_USER_ID) {
      toast.error("Email service isn't configured. Use the email link instead.");
      setIsSending(false);
      return;
    }

    const toastId = toast.loading("Sending...");

    try {
      emailjs
        .sendForm(
          EMAILJS_SERVICE_ID,
          EMAILJS_TEMPLATE_ID,
          form,
          EMAILJS_USER_ID,
        )
        .then(
        () => {
          toast.success("Message sent successfully!", { id: toastId });
          form.reset();
          setIsSending(false);
        },
        () => {
          toast.error("Failed to send message.", { id: toastId });
          setIsSending(false);
        },
      );
    } catch {
      toast.error("Failed to send message.", { id: toastId });
      setIsSending(false);
    }
  };

  const links = [
    {
      icon: Mail,
      label: "Email",
      value: contact.email,
      href: `mailto:${contact.email}`,
    },
    {
      icon: Github,
      label: "GitHub",
      value: `@${contact.github}`,
      href: `https://github.com/${contact.github}`,
    },
    {
      icon: Linkedin,
      label: "LinkedIn",
      value: `/in/${contact.linkedin}`,
      href: `https://www.linkedin.com/in/${contact.linkedin}`,
    },
  ];

  return (
    <section id="contact" className="scroll-mt-20 border-b border-paper-300 py-24 lg:py-32">
      <div className="mx-auto max-w-shell px-6 lg:px-10">
        <div className="grid gap-8 border-b border-paper-300 pb-10 lg:grid-cols-12 lg:items-end lg:gap-10">
          <div className="lg:col-span-7">
            <p className="eyebrow eyebrow-dot">{contact.eyebrow}</p>
            <h2 className="display mt-5 text-display-md text-ink-950">
              {contact.heading}
            </h2>
          </div>
          <p className="text-balance text-base leading-relaxed text-ink-600 lg:col-span-5">
            {contact.desc}
          </p>
        </div>

        <div className="grid gap-10 lg:grid-cols-12 lg:gap-8">
          {/* Form */}
          <form
            onSubmit={sendEmail}
            className="space-y-5 border border-paper-300 bg-white p-7 lg:col-span-7 lg:p-9"
          >
            <div className="grid gap-5 sm:grid-cols-2">
              <div>
                <label
                  htmlFor="name"
                  className="mb-2 block font-mono text-micro uppercase text-ink-500"
                >
                  Name
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  placeholder="Your name"
                  className={field}
                />
              </div>
              <div>
                <label
                  htmlFor="email"
                  className="mb-2 block font-mono text-micro uppercase text-ink-500"
                >
                  Email
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  placeholder="you@company.com"
                  className={field}
                />
              </div>
            </div>

            <div>
              <label
                htmlFor="message"
                className="mb-2 block font-mono text-micro uppercase text-ink-500"
              >
                Message
              </label>
              <textarea
                id="message"
                name="message"
                rows="6"
                placeholder="Tell me about the project"
                className={`${field} resize-none`}
              />
            </div>

            <button
              type="submit"
              disabled={isSending}
              className="group flex w-full items-center justify-center gap-2 bg-ink-950 px-7 py-4 font-display text-body font-semibold text-paper transition-colors hover:bg-clay disabled:cursor-not-allowed disabled:bg-paper-400 disabled:text-paper-100"
            >
              <Send size={16} strokeWidth={2.5} />
              {isSending ? "Sending…" : "Send message"}
            </button>

            <p className="text-center font-mono text-micro uppercase text-ink-500">
              Reply {contact.response}
            </p>
          </form>

          {/* Direct links + availability */}
          <div className="flex flex-col gap-4 lg:col-span-5">
            {links.map((l) => (
              <a
                key={l.label}
                href={l.href}
                target={l.href.startsWith("mailto") ? undefined : "_blank"}
                rel="noopener noreferrer"
                className="group flex items-center gap-4 border border-paper-300 bg-white p-5 transition-colors duration-200 hover:border-clay"
              >
                <span className="grid h-11 w-11 shrink-0 place-items-center border border-paper-400 text-ink-500 transition-colors duration-200 group-hover:border-ink-950 group-hover:bg-ink-950 group-hover:text-paper">
                  <l.icon size={18} />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block font-mono text-micro uppercase text-ink-500">
                    {l.label}
                  </span>
                  <span className="block truncate font-display text-body font-medium tracking-tight text-ink-950">
                    {l.value}
                  </span>
                </span>
              </a>
            ))}

            <div className="mt-1 border border-paper-300 bg-paper-100 p-6">
              <p className="font-mono text-micro uppercase text-ink-500">
                Open to
              </p>
              <ul className="mt-4 space-y-3">
                {contact.available.map((item) => (
                  <li
                    key={item}
                    className="flex items-center gap-3 text-body text-ink-700"
                  >
                    <span
                      aria-hidden
                      className="h-1.5 w-1.5 shrink-0 rounded-full bg-clay"
                    />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
